<template>
  <div id="app-multi-counter">
    <secondHeader />
    <div class="main-multi-counter">
      <sidebar />
      <div class="content-multi-counter">
        <notice-bar />
        <h1 class="multi-counter-page-title">カウンター</h1>
        <p class="multi-counter-page-subtitle">
          ラベル・単位を個別に設定できるカウンター。プレビューを確認しながらカスタマイズできます。
        </p>

        <!-- 2-column workspace -->
        <div class="mc-workspace">
          <!-- Left: Preview (sticky) -->
          <aside class="mc-preview-pane">
            <div class="mc-preview-sticky">
              <div class="mc-preview-header">
                <span class="mc-preview-title">プレビュー</span>
                <span class="mc-preview-direction">
                  {{ direction === 'row' ? '横並び' : '縦並び' }}
                </span>
              </div>
              <div
                class="mc-preview"
                :class="{ 'dir-row': direction === 'row', 'dir-column': direction === 'column' }"
                :style="{ fontFamily: cssFontFamily }"
              >
                <div
                  v-for="counter in counters"
                  :key="counter.id + '-preview'"
                  class="mc-preview-item"
                  :class="[
                    `mc-variant-${shapeVariantOf(counter)}`,
                    { 'is-selected': counter.id === selectedCounterId }
                  ]"
                  :style="[previewItemStyle(counter), { color: previewTextColor(counter) }]"
                  @click="selectCounter(counter.id)"
                >
                  <div v-if="counter.label" class="mc-item-header">
                    <span class="mc-item-label">
                      <span class="mc-item-deco" aria-hidden="true"></span>
                      <span class="mc-item-label-text">{{ counter.label }}</span>
                      <span class="mc-item-deco" aria-hidden="true"></span>
                    </span>
                    <div class="mc-item-divider"></div>
                  </div>
                  <div class="mc-item-value">
                    <span class="mc-item-count"
                      >{{ counter.count }}<span
                        v-if="counter.unit"
                        class="mc-item-unit"
                      >{{ counter.unit }}</span></span>
                  </div>
                </div>
              </div>
              <p class="mc-preview-hint">カウンターをクリックすると右で編集できます</p>
            </div>
          </aside>

          <!-- Right: Settings panel -->
          <section class="mc-settings-pane">
            <!-- Top tabs -->
            <div class="mc-tabs">
              <button
                class="mc-tab"
                :class="{ 'is-active': topTab === 'counters' }"
                @click="topTab = 'counters'"
              >個別設定</button>
              <button
                class="mc-tab"
                :class="{ 'is-active': topTab === 'theme' }"
                @click="topTab = 'theme'"
              >全体設定</button>
            </div>

            <!-- Counter settings -->
            <div v-if="topTab === 'counters'" class="mc-tab-body">
              <!-- Counter chip tabs -->
              <div class="mc-counter-chips">
                <button
                  v-for="(counter, index) in counters"
                  :key="counter.id"
                  class="mc-counter-chip"
                  :class="{ 'is-active': counter.id === selectedCounterId }"
                  @click="selectCounter(counter.id)"
                >
                  <span class="mc-chip-index">#{{ index + 1 }}</span>
                  <span class="mc-chip-label">{{ counter.label || '（無題）' }}</span>
                </button>
                <button
                  class="mc-counter-chip mc-counter-chip-add"
                  :disabled="counters.length >= maxCounters"
                  @click="addCounter"
                >+ 追加</button>
              </div>
              <p v-if="counters.length >= maxCounters" class="mc-limit-note">
                最大 {{ maxCounters }} 個までです
              </p>

              <!-- Selected counter editor -->
              <div v-if="selectedCounter" class="mc-editor">
                <div class="mc-editor-header">
                  <span class="mc-editor-title">
                    #{{ selectedIndex + 1 }} の編集
                  </span>
                  <button
                    class="mc-btn mc-btn-danger mc-btn-small"
                    :disabled="counters.length <= 1"
                    @click="removeCounter(selectedIndex)"
                  >このカウンターを削除</button>
                </div>

                <div class="mc-field-row">
                  <div class="mc-field">
                    <label>ラベル</label>
                    <input
                      v-model="selectedCounter.label"
                      type="text"
                      placeholder="例: 得点"
                      @input="persistCounters"
                    >
                  </div>
                  <div class="mc-field">
                    <label>単位</label>
                    <input
                      v-model="selectedCounter.unit"
                      type="text"
                      placeholder="例: 点 / 回 / pt"
                      @input="persistCounters"
                    >
                  </div>
                </div>

                <div class="mc-counter-ops">
                  <button class="mc-btn" @click="decrement(selectedIndex)">-</button>
                  <span class="mc-counter-value">
                    {{ selectedCounter.count }}<span
                      v-if="selectedCounter.unit"
                      class="mc-counter-value-unit"
                    >{{ selectedCounter.unit }}</span>
                  </span>
                  <button class="mc-btn" @click="increment(selectedIndex)">+</button>
                  <button
                    class="mc-btn mc-btn-danger mc-btn-small"
                    @click="resetCount(selectedIndex)"
                  >リセット</button>
                </div>

                <!-- Color & shape (always visible) -->
                <div class="mc-individual-body">
                  <div class="mc-field-row">
                    <div class="mc-field">
                      <label>背景形状</label>
                      <select
                        v-model="selectedCounter.backgroundShape"
                        @change="persistCounters"
                      >
                        <option
                          v-for="shape in shapeOptions"
                          :key="shape.id"
                          :value="shape.id"
                        >{{ shape.label }}</option>
                      </select>
                    </div>
                    <div class="mc-field">
                      <label>テーマ</label>
                      <select
                        v-model="selectedCounter.backgroundColor"
                        @change="onIndividualBgChange(selectedIndex)"
                      >
                        <optgroup
                          v-for="group in themeGroups"
                          :key="group.label"
                          :label="group.label"
                        >
                          <option
                            v-for="opt in group.options"
                            :key="opt.id"
                            :value="opt.id"
                          >{{ opt.name }}</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                  <div class="mc-picker-row">
                    <label>文字色</label>
                    <compact-color-picker
                      :value="selectedCounter.textColor"
                      @input="(hex) => onIndividualTextColor(selectedIndex, hex)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Theme settings -->
            <div v-if="topTab === 'theme'" class="mc-tab-body">
              <div class="mc-field">
                <label>フォント</label>
                <select v-model="selectedFont">
                  <option value="'DotGothic16', sans-serif">DotGothic16</option>
                  <option value="'Hachi Maru Pop', cursive">Hachi Maru Pop</option>
                  <option value="'Monomaniac One', sans-serif">Monomaniac One</option>
                  <option value="'Montserrat Alternates', sans-serif">Montserrat Alternates</option>
                </select>
              </div>

              <div class="mc-field">
                <label>表示方向</label>
                <div class="mc-radio-row">
                  <label class="mc-radio-item">
                    <input type="radio" value="row" v-model="direction">
                    横並び
                  </label>
                  <label class="mc-radio-item">
                    <input type="radio" value="column" v-model="direction">
                    縦並び
                  </label>
                </div>
              </div>

              <div class="mc-field-row">
                <div class="mc-field">
                  <label>テーマ</label>
                  <select v-model="themeBackgroundColor">
                    <optgroup
                      v-for="group in themeGroups"
                      :key="group.label"
                      :label="group.label"
                    >
                      <option
                        v-for="opt in group.options"
                        :key="opt.id"
                        :value="opt.id"
                      >{{ opt.name }}</option>
                    </optgroup>
                  </select>
                </div>
                <div class="mc-field">
                  <label>背景形状</label>
                  <select v-model="themeBackgroundShape">
                    <option
                      v-for="shape in shapeOptions"
                      :key="shape.id"
                      :value="shape.id"
                    >{{ shape.label }}</option>
                  </select>
                </div>
              </div>

              <div class="mc-picker-row">
                <label>テーマ文字色</label>
                <compact-color-picker
                  :value="themeTextColor"
                  @input="updateThemeTextColor"
                />
              </div>

              <div class="mc-apply-all">
                <p class="mc-apply-all-note">
                  上の全体設定を、今あるすべてのカウンターに反映します（個別にカスタマイズした色は上書きされます）。
                </p>
                <button class="mc-btn mc-btn-apply-all" @click="applyThemeToAll">
                  このテーマを全カウンターに適用
                </button>
              </div>
            </div>

            <!-- Copy link action (always visible) -->
            <div class="mc-action-bar">
              <button class="mc-copy-button" @click="copyLink">
                ブラウザソース用リンクをコピー
              </button>
            </div>
          </section>
        </div>

        <!-- Collapsible how-to -->
        <div class="mc-howto-wrapper">
          <button
            class="mc-collapse-header mc-howto-header"
            :class="{ 'is-open': howtoOpen }"
            :aria-expanded="howtoOpen ? 'true' : 'false'"
            @click="howtoOpen = !howtoOpen"
          >
            <span class="mc-collapse-label">
              <span class="mc-collapse-chevron">▸</span>
              使い方を見る
            </span>
            <span class="mc-collapse-indicator">{{ howtoOpen ? '閉じる' : '開く' }}</span>
          </button>
          <transition name="mc-collapse">
          <div v-if="howtoOpen" class="mc-howto-body">
            <p class="mc-howto-title">
              このページはカウンターのデザインをカスタマイズするためのページです。カスタマイズしたら下記の手順で OBS に追加していきます。
            </p>
            <p>1. OBS 上部の「ドック」から「カスタムブラウザドック」を開きます。</p>
            <p>2. ドック名に「カウンター」、URL に「https://ruri.info/tool/multicountercontrol」を入力し適用を押します。</p>
            <p>3. カウンター一覧のウィンドウが表示されます。これがカウントのためのコントローラーです。好きな場所に配置します。</p>
            <p>4. このページで各カウンターのラベル・単位・デザインをカスタマイズしたら、上の「ブラウザソース用リンクをコピー」ボタンを押します。</p>
            <p>5. OBS のブラウザソースを追加し、URL 欄にコピーしたリンクを貼り付けます。</p>
            <p>以上で導入完了です。コントローラーで更新した値がブラウザソースに反映されます。</p>
          </div>
          </transition>
        </div>

        <div class="blog-area">
          <h2 class="blog-area-title">使い方はブログでチェック！</h2>
          <div class="blog-area-row">
            <div class="blog-area-thumb">
              <img class="article-list-img" src="@/assets/article/thomnail-counter.png">
            </div>
            <div class="blog-area-desc">
              <p>具体的な使い方や、おすすめのカスタマイズ方法はブログ記事で紹介しています。あわせてどうぞ！</p>
              <div class="blog-area-btn">
                <a href="https://www.ruri.info/counter/" target="_blank" rel="noopener noreferrer">
                  <span class="check-btn">記事をチェック</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="ad-block">
          <div class="ad">
            <InFeedAdsense
              data-ad-layout-key="-fb+5w+4e-db+86"
              data-ad-client="ca-pub-9016794589237931"
              data-ad-slot="4898735474">
            </InFeedAdsense>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import secondHeader from "../components/secondHeader.vue"
import sidebar from "../components/sidebar.vue"
import noticeBar from "../components/notice-bar.vue"
import CompactColorPicker from "../components/CompactColorPicker.vue"
import {
  THEME_GROUPS,
  THEME_PRESETS,
  SHAPE_OPTIONS,
  buildItemStyle,
  defaultTextColorFor,
  shapeVariant
} from '../utils/multiCounterThemes'

const STORAGE_KEY = 'multiCounters'
const THEME_KEY = 'multiCounterTheme'

function createDefaultCounter(index = 0, defaults = {}) {
  const presetId = defaults.backgroundColor || 'sakura'
  return {
    id: `c${Date.now()}${Math.floor(Math.random() * 1000)}`,
    label: `カウンター${index + 1}`,
    unit: '',
    count: 0,
    backgroundShape: defaults.backgroundShape || 'rounded',
    backgroundColor: presetId,
    textColor: defaults.textColor || defaultTextColorFor(presetId)
  }
}

function loadCounters(themeDefaults = {}) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return [createDefaultCounter(0, themeDefaults)]
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [createDefaultCounter(0, themeDefaults)]
    }
    return parsed.map((c, i) => {
      const base = createDefaultCounter(i, themeDefaults)
      // Legacy migration: counters that used the shared theme should carry
      // those values explicitly now that the toggle has been removed.
      if (c.useTheme === true) {
        return {
          ...base,
          ...c,
          backgroundShape: themeDefaults.backgroundShape || base.backgroundShape,
          backgroundColor: themeDefaults.backgroundColor || base.backgroundColor,
          textColor: themeDefaults.textColor || base.textColor,
          useTheme: undefined
        }
      }
      const merged = { ...base, ...c }
      delete merged.useTheme
      return merged
    })
  } catch (e) {
    return [createDefaultCounter(0, themeDefaults)]
  }
}

const DEFAULT_THEME = {
  font: "'Hachi Maru Pop', cursive",
  direction: 'row',
  themeBackgroundShape: 'rounded',
  themeBackgroundColor: 'sakura',
  themeTextColor: defaultTextColorFor('sakura')
}

function loadTheme() {
  try {
    const raw = localStorage.getItem(THEME_KEY)
    if (!raw) return { ...DEFAULT_THEME }
    const parsed = JSON.parse(raw)
    return { ...DEFAULT_THEME, ...parsed }
  } catch (e) {
    return { ...DEFAULT_THEME }
  }
}

export default {
  components: {
    secondHeader,
    sidebar,
    noticeBar,
    CompactColorPicker
  },
  data() {
    const theme = loadTheme()
    const counters = loadCounters({
      backgroundShape: theme.themeBackgroundShape,
      backgroundColor: theme.themeBackgroundColor,
      textColor: theme.themeTextColor
    })
    return {
      counters,
      maxCounters: 10,
      selectedCounterId: counters[0].id,
      topTab: 'counters',
      howtoOpen: false,
      selectedFont: theme.font,
      direction: theme.direction,
      themeBackgroundShape: theme.themeBackgroundShape,
      themeBackgroundColor: theme.themeBackgroundColor,
      themeTextColor: theme.themeTextColor
    }
  },
  computed: {
    selectedIndex() {
      return this.counters.findIndex(c => c.id === this.selectedCounterId)
    },
    selectedCounter() {
      const idx = this.selectedIndex
      return idx >= 0 ? this.counters[idx] : null
    },
    themeGroups() {
      return THEME_GROUPS.map(group => ({
        label: group.label,
        options: group.ids
          .filter(id => THEME_PRESETS[id])
          .map(id => ({ id, name: THEME_PRESETS[id].name }))
      }))
    },
    shapeOptions() {
      return SHAPE_OPTIONS
    },
    // Append a Japanese system-font fallback chain so kanji/kana don't
    // collapse to a generic ugly fallback when a Latin-only font is picked.
    cssFontFamily() {
      const fallback = "'Hiragino Sans','Hiragino Kaku Gothic ProN','Yu Gothic',YuGothic,'Meiryo',sans-serif"
      return `${this.selectedFont},${fallback}`
    }
  },
  watch: {
    selectedFont() { this.persistTheme() },
    direction() { this.persistTheme() },
    themeBackgroundShape() { this.persistTheme() },
    themeBackgroundColor(newVal) {
      this.themeTextColor = defaultTextColorFor(newVal)
      this.persistTheme()
    }
  },
  mounted() {
    this.persistCounters()
    this.persistTheme()
    window.addEventListener('storage', this.handleStorageChange)
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    persistCounters() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.counters))
    },
    persistTheme() {
      const theme = {
        font: this.selectedFont,
        direction: this.direction,
        themeBackgroundShape: this.themeBackgroundShape,
        themeBackgroundColor: this.themeBackgroundColor,
        themeTextColor: this.themeTextColor
      }
      localStorage.setItem(THEME_KEY, JSON.stringify(theme))
    },
    handleStorageChange(event) {
      if (event.key === STORAGE_KEY) {
        const next = loadCounters({
          backgroundShape: this.themeBackgroundShape,
          backgroundColor: this.themeBackgroundColor,
          textColor: this.themeTextColor
        })
        this.counters = next
        if (!next.find(c => c.id === this.selectedCounterId)) {
          this.selectedCounterId = next[0] ? next[0].id : null
        }
      } else if (event.key === THEME_KEY) {
        const theme = loadTheme()
        this.selectedFont = theme.font
        this.direction = theme.direction
        this.themeBackgroundShape = theme.themeBackgroundShape
        this.themeBackgroundColor = theme.themeBackgroundColor
        this.themeTextColor = theme.themeTextColor
      }
    },

    selectCounter(id) {
      this.selectedCounterId = id
      if (this.topTab !== 'counters') this.topTab = 'counters'
    },

    increment(index) {
      this.counters[index].count++
      this.persistCounters()
    },
    decrement(index) {
      this.counters[index].count--
      this.persistCounters()
    },
    resetCount(index) {
      this.counters[index].count = 0
      this.persistCounters()
    },
    addCounter() {
      if (this.counters.length >= this.maxCounters) return
      const fresh = createDefaultCounter(this.counters.length, {
        backgroundShape: this.themeBackgroundShape,
        backgroundColor: this.themeBackgroundColor,
        textColor: this.themeTextColor
      })
      this.counters.push(fresh)
      this.selectedCounterId = fresh.id
      this.persistCounters()
    },
    removeCounter(index) {
      if (this.counters.length <= 1) return
      const removedId = this.counters[index].id
      this.counters.splice(index, 1)
      if (removedId === this.selectedCounterId) {
        const nextIdx = Math.min(index, this.counters.length - 1)
        this.selectedCounterId = this.counters[nextIdx].id
      }
      this.persistCounters()
    },
    applyThemeToAll() {
      this.counters = this.counters.map(c => ({
        ...c,
        backgroundShape: this.themeBackgroundShape,
        backgroundColor: this.themeBackgroundColor,
        textColor: this.themeTextColor
      }))
      this.persistCounters()
    },
    onIndividualBgChange(index) {
      const c = this.counters[index]
      this.$set(c, 'textColor', defaultTextColorFor(c.backgroundColor))
      this.persistCounters()
    },
    onIndividualTextColor(index, hex) {
      const c = this.counters[index]
      if (typeof hex === 'string' && hex) {
        this.$set(c, 'textColor', hex)
        this.persistCounters()
      }
    },
    updateThemeTextColor(hex) {
      if (typeof hex === 'string' && hex) {
        this.themeTextColor = hex
        this.persistTheme()
      }
    },

    previewItemStyle(counter) {
      return buildItemStyle(counter.backgroundColor, counter.backgroundShape)
    },
    previewTextColor(counter) {
      return counter.textColor
    },
    shapeVariantOf(counter) {
      return shapeVariant(counter.backgroundShape)
    },

    copyLink() {
      const url = new URL(window.location.href)
      url.pathname = '/tool/multicounterdesign'
      url.search = ''
      url.searchParams.set('font', this.selectedFont)
      url.searchParams.set('dir', this.direction)
      // Embed the full design (per-counter visuals + labels) so OBS can render
      // the configured counters even before the control dock populates
      // localStorage. `count` is omitted — that's always live via the dock.
      //
      // Keys are shortened and the payload is encoded as base64url so the
      // URL stays as compact as possible (no + / = that would get
      // percent-encoded by URLSearchParams).
      const cfg = this.counters.map(c => ({
        i: c.id,
        l: c.label,
        u: c.unit,
        s: c.backgroundShape,
        c: c.backgroundColor,
        t: c.textColor
      }))
      try {
        const json = JSON.stringify(cfg)
        const b64url = btoa(unescape(encodeURIComponent(json)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '')
        url.searchParams.set('cfg', b64url)
      } catch (e) {
        // If encoding fails for any reason, fall back to URL without cfg.
      }
      navigator.clipboard.writeText(url.toString()).then(() => {
        alert('リンクがコピーされました')
      })
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=DotGothic16&family=Hachi+Maru+Pop&family=Monomaniac+One&family=Montserrat+Alternates:wght@800&display=swap');

#app-multi-counter {
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  margin: 0 auto;
}

.main-multi-counter {
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  margin: 0 auto;
}

.content-multi-counter {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 60px 0 60px 1.82%;
  margin: 0 auto;
  max-width: 1100px;
  width: 100%;
  box-sizing: border-box;
}

.multi-counter-page-title {
  text-align: center;
  letter-spacing: 2px;
  text-shadow: 2px 2px 6px rgb(210 210 210 / 80%);
  font-size: 1.5rem;
  color: #212121;
  font-weight: bold;
}

.multi-counter-page-subtitle {
  text-align: center;
  color: #555;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

/* Workspace 2-column layout */
.mc-workspace {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(340px, 1.1fr);
  gap: 20px;
  padding: 0 16px;
  align-items: start;
}

/* ---- Preview pane (left) ---- */
.mc-preview-pane {
  min-width: 0;
}

.mc-preview-sticky {
  position: sticky;
  top: 20px;
  background: #fafafa;
  border: 1px dashed #bbb;
  border-radius: 10px;
  padding: 14px;
}

.mc-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.mc-preview-title {
  font-weight: bold;
  color: #4c5c8a;
}

.mc-preview-direction {
  font-size: 0.8rem;
  color: #777;
}

.mc-preview {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 8px 0;
}

.mc-preview.dir-row {
  flex-direction: row;
  flex-wrap: wrap;
}

.mc-preview.dir-column {
  flex-direction: column;
}

.mc-preview-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: 200px;
  padding: 22px 24px;
  box-sizing: border-box;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.mc-preview-item:hover {
  transform: translateY(-2px);
}

.mc-preview-item.is-selected {
  outline: 2px solid #4c5c8a;
  outline-offset: 6px;
}

.mc-item-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  width: 100%;
}

.mc-item-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 1.15rem;
  font-weight: bold;
  letter-spacing: 0.05em;
  line-height: 1.3;
  font-feature-settings: "palt" 1;
  text-rendering: optimizeLegibility;
}

.mc-item-deco {
  display: inline-block;
  width: 7px;
  height: 7px;
  background: var(--mc-accent, currentColor);
  transform: rotate(45deg);
  opacity: 0.9;
  flex: 0 0 auto;
}

.mc-item-label-text {
  white-space: nowrap;
}

.mc-item-divider {
  width: 60%;
  max-width: 120px;
  height: 1px;
  background: var(--mc-accent, currentColor);
  opacity: 0.45;
}

.mc-item-value {
  text-align: center;
  line-height: 1;
}

.mc-item-count {
  display: inline-block;
  position: relative;
  font-size: 3.6rem;
  line-height: 1;
  font-weight: bold;
}

.mc-item-unit {
  position: absolute;
  left: 100%;
  bottom: 0.18em;
  margin-left: 4px;
  font-size: 1.3rem;
  line-height: 1;
  opacity: 0.75;
  font-weight: normal;
  white-space: nowrap;
}

/* ---- Bar variant (wide & short, with floating label badge) ---- */
.mc-preview-item.mc-variant-bar {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  min-height: auto;
  padding: 18px 12px 10px;
  gap: 0;
  overflow: visible;
}

.mc-preview-item.mc-variant-bar .mc-item-header {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  width: auto;
  margin-bottom: 0;
  gap: 0;
}

.mc-preview-item.mc-variant-bar .mc-item-label {
  background: var(--mc-label-bg, currentColor);
  color: var(--mc-label-color, #fff);
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.95rem;
  letter-spacing: 0.03em;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
  white-space: nowrap;
}

.mc-preview-item.mc-variant-bar .mc-item-deco {
  background: var(--mc-label-color, #fff);
  opacity: 0.85;
  width: 5px;
  height: 5px;
}

.mc-preview-item.mc-variant-bar .mc-item-divider {
  display: none;
}

.mc-preview-item.mc-variant-bar .mc-item-value {
  flex: 0 0 auto;
}

.mc-preview-item.mc-variant-bar .mc-item-count {
  font-size: 2.6rem;
}

.mc-preview-item.mc-variant-bar .mc-item-unit {
  font-size: 1rem;
  bottom: 0.16em;
  margin-left: 3px;
}

.mc-preview-hint {
  font-size: 0.75rem;
  color: #888;
  text-align: center;
  margin: 8px 0 0;
}

/* ---- Settings pane (right) ---- */
.mc-settings-pane {
  min-width: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}

.mc-tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  background: #f5f5f7;
}

.mc-tab {
  flex: 1;
  padding: 12px 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  color: #555;
  border-bottom: 3px solid transparent;
  transition: all 0.15s;
}

.mc-tab:hover {
  background: #eee;
}

.mc-tab.is-active {
  background: #fff;
  color: #4c5c8a;
  border-bottom-color: #4c5c8a;
  font-weight: bold;
}

.mc-tab-body {
  padding: 16px;
}

/* Counter chip tabs */
.mc-counter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}

.mc-counter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f0f0f3;
  border: 1px solid #ddd;
  border-radius: 18px;
  cursor: pointer;
  font-size: 0.82rem;
  max-width: 220px;
  transition: all 0.15s;
}

.mc-counter-chip:hover:not(:disabled) {
  background: #e8e8ed;
}

.mc-counter-chip.is-active {
  background: #4c5c8a;
  border-color: #4c5c8a;
  color: #fff;
}

.mc-counter-chip-add {
  background: #fff;
  border-style: dashed;
  color: #4c5c8a;
}

.mc-counter-chip-add:disabled {
  color: #aaa;
  cursor: not-allowed;
}

.mc-chip-index {
  font-weight: bold;
}

.mc-chip-label {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mc-limit-note {
  font-size: 0.75rem;
  color: #777;
  margin: 0 0 12px;
}

/* Editor */
.mc-editor {
  border-top: 1px solid #eee;
  padding-top: 14px;
}

.mc-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mc-editor-title {
  font-weight: bold;
  color: #333;
}

.mc-field-row {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.mc-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 140px;
  margin-bottom: 12px;
}

.mc-field label {
  font-size: 0.8rem;
  color: #555;
  margin-bottom: 4px;
}

.mc-field input,
.mc-field select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
  width: 100%;
  box-sizing: border-box;
}

.mc-counter-ops {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin: 14px 0;
  flex-wrap: wrap;
  background: #f8f8fa;
  padding: 10px;
  border-radius: 6px;
}

.mc-counter-value {
  font-size: 1.3rem;
  min-width: 60px;
  text-align: center;
  font-weight: bold;
}

.mc-counter-value-unit {
  font-size: 0.85rem;
  color: #555;
  margin-left: 4px;
  font-weight: normal;
}

.mc-btn {
  padding: 6px 14px;
  font-size: 1rem;
  background: #4c5c8a;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.mc-btn:hover:not(:disabled) {
  background: #3b4b74;
}

.mc-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.mc-btn-small {
  font-size: 0.8rem;
  padding: 5px 10px;
}

.mc-btn-danger {
  background: #d9534f;
}

.mc-btn-danger:hover:not(:disabled) {
  background: #c9302c;
}

/* Always-visible color & shape section */
.mc-individual-body {
  margin-top: 12px;
  padding: 14px 12px;
  background: #fafafc;
  border: 1px solid #e6e6ee;
  border-radius: 8px;
}

/* Bulk-apply theme button */
.mc-apply-all {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e0e0e5;
}

.mc-apply-all-note {
  font-size: 0.78rem;
  color: #777;
  line-height: 1.5;
  margin: 0 0 10px;
}

.mc-btn-apply-all {
  display: block;
  width: 100%;
  padding: 10px 14px;
  background: #4c5c8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mc-btn-apply-all:hover {
  background: #3f4e78;
}

.mc-collapse-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: #f5f5f7;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  transition: background 0.18s ease, border-color 0.18s ease, border-radius 0.18s ease;
  user-select: none;
}

.mc-collapse-header:hover {
  background: #ececf2;
  border-color: #d0d0d8;
}

.mc-collapse-header.is-open {
  background: #e6e6ef;
  border-color: #4c5c8a;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  color: #4c5c8a;
  font-weight: bold;
}

.mc-collapse-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mc-collapse-chevron {
  display: inline-block;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
  font-size: 0.9rem;
  line-height: 1;
  color: #777;
}

.mc-collapse-header.is-open .mc-collapse-chevron {
  transform: rotate(90deg);
  color: #4c5c8a;
}

.mc-collapse-indicator {
  font-size: 0.72rem;
  padding: 2px 8px;
  border-radius: 10px;
  background: #d9d9df;
  color: #555;
  transition: background 0.18s ease, color 0.18s ease;
}

.mc-collapse-header.is-open .mc-collapse-indicator {
  background: #4c5c8a;
  color: #fff;
}

.mc-collapse-body {
  padding: 14px 12px;
  background: #fafafc;
  border: 1px solid #4c5c8a;
  border-top: none;
  border-radius: 0 0 8px 8px;
  margin-top: -1px;
}

/* Slide-down transition for collapsible bodies */
.mc-collapse-enter-active,
.mc-collapse-leave-active {
  transition: max-height 0.22s ease, opacity 0.18s ease, padding 0.22s ease;
  overflow: hidden;
}

.mc-collapse-enter,
.mc-collapse-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.mc-collapse-enter-to,
.mc-collapse-leave {
  max-height: 1200px;
  opacity: 1;
}

.mc-picker-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
}

.mc-picker-row label {
  font-size: 0.8rem;
  color: #555;
}

.mc-radio-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.mc-radio-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  white-space: nowrap;
  cursor: pointer;
}

/* Action bar */
.mc-action-bar {
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  display: flex;
  justify-content: center;
}

.mc-copy-button {
  padding: 10px 20px;
  background-color: #4c5c8a;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 0.95rem;
  font-weight: bold;
}

.mc-copy-button:hover {
  background-color: #3b466a;
}

/* How-to */
.mc-howto-wrapper {
  max-width: 900px;
  margin: 24px auto 0;
  padding: 0 16px;
  width: 100%;
  box-sizing: border-box;
}

.mc-howto-header {
  font-weight: bold;
}

.mc-howto-body {
  padding: 14px 16px;
  line-height: 1.7;
  font-size: 0.9rem;
  color: #333;
  background: #fafafc;
  border: 1px solid #4c5c8a;
  border-top: none;
  border-radius: 0 0 8px 8px;
  margin-top: -1px;
}

.mc-howto-title {
  margin-bottom: 10px;
  font-weight: bold;
}

/* Responsive: stack on narrow screens */
@media (max-width: 960px) {
  .mc-workspace {
    grid-template-columns: 1fr;
  }
  .mc-preview-sticky {
    position: sticky;
    top: 0;
    z-index: 10;
    border-radius: 0 0 10px 10px;
  }
  .mc-preview-item {
    min-width: 170px;
    min-height: 170px;
    padding: 16px 18px;
  }
  .mc-item-count {
    font-size: 2.8rem;
  }
  .mc-item-unit {
    font-size: 1.1rem;
  }
  .mc-preview-item.mc-variant-bar {
    min-width: 160px;
    min-height: auto;
    padding: 16px 10px 8px;
  }
  .mc-preview-item.mc-variant-bar .mc-item-count {
    font-size: 2.2rem;
  }
  .mc-preview-item.mc-variant-bar .mc-item-unit {
    font-size: 0.85rem;
  }
}
</style>
